import {
  collection,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  type Unsubscribe,
} from 'firebase/firestore';
import { ContactSubmissionSchema, type ContactSubmission } from '@etiquetachef/types';
import { firestore } from './firebase';

const TAG = 'admin/contact';

/** Teto de itens carregados (ordenados por `createdAt desc`). */
export const CONTACT_LIST_LIMIT = 200;

/** Mensagem de contato com id e `createdAt` em millis. */
export type ContactSubmissionRecord = ContactSubmission & {
  id: string;
  createdAtMillis: number | null;
};

/** Estado da lista de mensagens da tela de contato. */
export type ContactSubmissionsResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; items: ContactSubmissionRecord[] }
  | { kind: 'ERROR'; error: unknown };

/** Materializa um Firestore Timestamp (ou número) em millis. */
function tsToMillis(at: unknown): number | null {
  if (typeof at === 'object' && at !== null && 'toMillis' in at) {
    return (at as { toMillis: () => number }).toMillis();
  }
  if (typeof at === 'number') return at;
  return null;
}

/**
 * Assina `/contactSubmissions` ordenado por mais recente.
 *
 * Origem: sem issue — commit aae23995a, 2026-06-18 (tela de contato).
 *
 * Contrato: até `CONTACT_LIST_LIMIT` docs, `createdAt desc`; doc fora do
 * schema é pulado; erro de snapshot → `ERROR`.
 *
 * Usado por: `features/contact/useContactSubmissions.ts` (tela `/contact`,
 * spec `docs/screens/admin-contact.md`).
 *
 * Arquitetura: rule `match /contactSubmissions/{id}` — leitura super-admin,
 * escrita `false` (grava a Function do formulário da landing). `orderBy` de
 * campo único: índice automático.
 *
 * Dados: `contactSubmissions/{id}` — `ContactSubmissionSchema` (nome, e-mail,
 * mensagem, `createdAt` serverTimestamp). Dado pessoal: não levar para log.
 *
 * Falhas conhecidas:
 *  - Mensagem fora do schema some só com `console.warn`, sem report (#926).
 */
export function subscribeContactSubmissions(
  onResult: (r: ContactSubmissionsResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'contactSubmissions');
  const q = query(ref, orderBy('createdAt', 'desc'), fsLimit(CONTACT_LIST_LIMIT));
  return onSnapshot(
    q,
    (snap) => {
      const items: ContactSubmissionRecord[] = [];
      for (const d of snap.docs) {
        const parsed = ContactSubmissionSchema.safeParse(d.data());
        if (!parsed.success) {
          console.warn(`${TAG} submission ${d.id} ignorado`, parsed.error.issues);
          continue;
        }
        items.push({ ...parsed.data, id: d.id, createdAtMillis: tsToMillis(parsed.data.createdAt) });
      }
      onResult({ kind: 'READY', items });
    },
    (err) => {
      console.error(`${TAG} submissions error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}
